import { useState } from "react";
import { Card, btnSecondary, btnDanger, inputStyle, labelStyle } from "../shared";

export function AccountManagementSection() {
  const [showDeactivate,   setShowDeactivate]   = useState(false);
  const [deactivatePw,     setDeactivatePw]     = useState("");
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [deactivateErr,    setDeactivateErr]    = useState("");

  const [deleteStep,       setDeleteStep]       = useState(0); // 0=closed 1=warning 2=confirm
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [deletePw,         setDeletePw]         = useState("");
  const [deleteLoading,    setDeleteLoading]    = useState(false);
  const [deleteErr,        setDeleteErr]        = useState("");

  const handleDeactivate = async () => {
    if (!deactivatePw) return;
    setDeactivateLoading(true);
    setDeactivateErr("");
    try {
      const { supabase: sb } = await import("../../supabaseClient");
      const { data, error } = await sb.functions.invoke("account-self-service", {
        body: { action: "deactivate", password: deactivatePw },
      });
      if (error || data?.error) {
        setDeactivateErr(data?.error || error?.message || "Something went wrong");
        return;
      }
      await sb.auth.signOut();
    } catch (err) {
      setDeactivateErr(String(err));
    } finally {
      setDeactivateLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE" || !deletePw) return;
    setDeleteLoading(true);
    setDeleteErr("");
    try {
      const { supabase: sb } = await import("../../supabaseClient");
      const { data, error } = await sb.functions.invoke("account-self-service", {
        body: { action: "delete-account", password: deletePw },
      });
      if (error || data?.error) {
        setDeleteErr(data?.error || error?.message || "Something went wrong");
        return;
      }
      await sb.auth.signOut();
      window.location.replace("/");
    } catch (err) {
      setDeleteErr(String(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <>
      <Card style={{ marginBottom: 20, borderColor: "#F5C6C6" }}>
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 800, fontSize: 16, marginBottom: 6, color: "#7f1d1d" }}>
          Account Management
        </div>
        <div style={{ fontSize: 12, color: "#9B9690", marginBottom: 20, lineHeight: 1.6 }}>
          Manage your account status. Deactivated accounts can be reactivated at any time. Deletion is permanent and cannot be undone.
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <button
            onClick={() => { setShowDeactivate(true); setDeactivatePw(""); setDeactivateErr(""); }}
            style={{ padding: "9px 20px", background: "transparent", color: "#9B9690", border: "1.5px solid #D4CFC7", borderRadius: 999, fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Deactivate Account
          </button>
          <button
            onClick={() => { setDeleteStep(1); setDeleteConfirmText(""); setDeletePw(""); setDeleteErr(""); }}
            style={{ ...btnDanger, borderRadius: 999, padding: "9px 20px", fontSize: 12 }}
          >
            Delete Account Permanently
          </button>
        </div>
      </Card>

      {/* Deactivate modal */}
      {showDeactivate && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", border: "1px solid #E8E4DC", borderRadius: 16, padding: 32, width: 440, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 22, color: "#2B2B2B", marginBottom: 8 }}>Deactivate Account</div>
            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 20, lineHeight: 1.6 }}>
              Your account will be paused and you will be signed out. All your data is preserved — you can reactivate anytime by signing in again.
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Enter your password to confirm</label>
              <input
                type="password"
                value={deactivatePw}
                onChange={e => setDeactivatePw(e.target.value)}
                placeholder="Your current password"
                style={inputStyle}
                autoFocus
              />
            </div>
            {deactivateErr && (
              <div style={{ fontSize: 13, color: "#dc2626", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                {deactivateErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowDeactivate(false)} style={btnSecondary}>Cancel</button>
              <button
                onClick={handleDeactivate}
                disabled={deactivateLoading || !deactivatePw}
                style={{ padding: "9px 20px", background: deactivatePw && !deactivateLoading ? "#374151" : "#9B9690", color: "#fff", border: "none", borderRadius: 999, fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700, cursor: deactivatePw && !deactivateLoading ? "pointer" : "default", opacity: deactivatePw ? 1 : 0.5 }}
              >
                {deactivateLoading ? "Deactivating…" : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal — step 1: warning */}
      {deleteStep === 1 && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", border: "1px solid #fca5a5", borderRadius: 16, padding: 32, width: 480, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 22, color: "#7f1d1d", marginBottom: 12 }}>Delete Account Permanently</div>
            <div style={{ background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "14px 16px", marginBottom: 18 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: "#dc2626", marginBottom: 8 }}>This cannot be undone. The following will be deleted:</div>
              <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#9B9690", lineHeight: 2 }}>
                <li>All properties and tenant records</li>
                <li>All payment history and financial data</li>
                <li>All maintenance tickets and documents</li>
                <li>All team members and settings</li>
                <li>Your company profile and uploaded files</li>
                <li>Your login credentials</li>
              </ul>
            </div>
            <p style={{ fontSize: 13, color: "#9B9690", marginBottom: 20, lineHeight: 1.6 }}>
              Your Stripe account will not be affected — only Leasly's connection to it is removed.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteStep(0)} style={btnSecondary}>Cancel</button>
              <button
                onClick={() => setDeleteStep(2)}
                style={{ ...btnDanger, borderRadius: 999, padding: "9px 20px", fontSize: 12 }}
              >
                Continue to Deletion
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete modal — step 2: type DELETE + password */}
      {deleteStep === 2 && (
        <div style={{ position: "fixed", inset: 0, background: "#00000088", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", border: "1px solid #fca5a5", borderRadius: 16, padding: 32, width: 440, maxWidth: "95vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 700, fontSize: 22, color: "#7f1d1d", marginBottom: 18 }}>Confirm Deletion</div>
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Type DELETE to confirm</label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                style={{ ...inputStyle, borderColor: deleteConfirmText === "DELETE" ? "#22c55e" : undefined }}
                autoFocus
              />
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={labelStyle}>Enter your password</label>
              <input
                type="password"
                value={deletePw}
                onChange={e => setDeletePw(e.target.value)}
                placeholder="Your current password"
                style={inputStyle}
              />
            </div>
            {deleteErr && (
              <div style={{ fontSize: 13, color: "#dc2626", background: "#FEF2F2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                {deleteErr}
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteStep(0)} style={btnSecondary}>Cancel</button>
              <button
                onClick={handleDeleteAccount}
                disabled={deleteLoading || deleteConfirmText !== "DELETE" || !deletePw}
                style={{ padding: "9px 20px", background: (deleteConfirmText === "DELETE" && deletePw && !deleteLoading) ? "#dc2626" : "#9B9690", color: "#fff", border: "none", borderRadius: 999, fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700, cursor: (deleteConfirmText === "DELETE" && deletePw && !deleteLoading) ? "pointer" : "default" }}
              >
                {deleteLoading ? "Deleting…" : "Delete My Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
